import { useEffect, useMemo, useState, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import type { CustomerBulkPatch, CustomerCollectionRecord, CustomerStatus, CustomerType } from '@shared/types';
import { subscribeToShellDeleteRequests } from '@renderer/app/events/shell-delete-events';
import { subscribeToShellDomainInvalidations } from '@renderer/app/events/shell-domain-events';
import { InlineQuickAddPanel, createQuickAddContext } from '@renderer/app/quick-add/quick-add-shared';
import { useShellStore } from '@renderer/app/state/shell-store';
import { BulkEditorRail } from '@renderer/shared/components/bulk-editor-rail';
import {
  CollectionColumnSettingsModal,
  resolveVisibleCollectionColumns,
  type CollectionColumnConfig
} from '@renderer/shared/components/collection-column-settings';
import {
  CardGridSurface,
  CollectionSelectionToggle,
  CollectionToolbar,
  CollectionViewToggle,
  DataTableSurface,
  EmptyCollectionState
} from '@renderer/shared/components/collection-scaffold';
import { CollectionTable, type CollectionSortState } from '@renderer/shared/components/collection-table';
import { Icon } from '@renderer/shared/components/icons';
import { SplitWorkspaceLayout } from '@renderer/shared/components/split-workspace';
import {
  FilterChip,
  IconButton,
  OverflowMenuButton,
  PanelCard,
  SectionHeader,
  SearchableSelectField,
  SelectField,
  StatusBadge,
  ToolbarGroup
} from '@renderer/shared/components/primitives';
import { useI18n } from '@renderer/shared/i18n';
import { openExternal, toTelUrl } from '@renderer/shared/utils/open-external';
import { applyBulkCustomerPatch, deleteCustomers, listCustomers } from './customers-repository';
import {
  selectCustomerRows,
  selectCustomerSelectionItems,
  selectCustomerCategoryOptions,
  selectCustomersSummary,
  type CustomerTableRowViewModel
} from './customers-selectors';
import { useCustomersViewStore, type CustomersSort } from './customers-view-store';

type PatchMode = 'unchanged' | 'clear' | 'set';
type SortDirection = 'asc' | 'desc';
type CustomerBulkDraft = Omit<CustomerBulkPatch, 'customerIds'>;

const customerStatuses: CustomerStatus[] = ['active', 'inactive', 'lead', 'archived'];
const customerTypes: CustomerType[] = ['company', 'sole_trader', 'individual'];

function createInitialCustomerBulkDraft(): CustomerBulkDraft {
  return {
    status: { mode: 'unchanged' },
    type: { mode: 'unchanged' },
    customerCategoryId: { mode: 'unchanged' },
    vatPayer: { mode: 'unchanged' }
  };
}

function customerSortToColumnId(sort: CustomersSort): string | null {
  return sort === 'name' ? 'name' : null;
}

function customerColumnIdToSort(columnId: string): CustomersSort | null {
  return columnId === 'name' ? 'name' : null;
}

function getDefaultCustomerSortDirection(sort: CustomersSort): SortDirection {
  return sort === 'name' ? 'asc' : 'desc';
}

export function CustomersScreen(): JSX.Element {
  const { t } = useI18n();
  const preferences = useShellStore((state) => state.preferences);
  const savePreferences = useShellStore((state) => state.savePreferences);
  const [collection, setCollection] = useState<CustomerCollectionRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [customerBulkDraft, setCustomerBulkDraft] = useState<CustomerBulkDraft>(createInitialCustomerBulkDraft);
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);
  const {
    statusFilter,
    categoryFilter,
    sort,
    selectionSession,
    setSort,
    setStatusFilter,
    setCategoryFilter,
    setViewMode,
    syncSelectionQuery,
    toggleSelection,
    removeSelectedId,
    clearSelection
  } = useCustomersViewStore();
  const [sortDirection, setSortDirection] = useState<SortDirection>(getDefaultCustomerSortDirection(sort));

  useEffect(() => {
    let active = true;

    setError(null);
    void listCustomers()
      .then((nextCollection) => {
        if (active) {
          setCollection(nextCollection);
        }
      })
      .catch((nextError) => {
        if (active) {
          setError(nextError instanceof Error ? nextError.message : 'Customers unavailable.');
        }
      });

    return () => {
      active = false;
    };
  }, [reloadToken]);

  useEffect(() => {
    return () => {
      clearSelection();
      setCustomerBulkDraft(createInitialCustomerBulkDraft());
    };
  }, [clearSelection]);

  useEffect(
    () =>
      subscribeToShellDomainInvalidations((detail) => {
        if (detail.domain === 'customers') {
          setReloadToken((value) => value + 1);
        }
      }),
    []
  );

  const summary = useMemo(() => (collection ? selectCustomersSummary(collection) : null), [collection]);
  const rows = useMemo(() => {
    if (!collection) {
      return [];
    }

    return selectCustomerRows(collection, { statusFilter, categoryFilter, sort, sortDirection, t });
  }, [categoryFilter, collection, sort, sortDirection, statusFilter, t]);
  const customerColumns = useMemo<CollectionColumnConfig[]>(
    () => [
      { id: 'selection', label: '', className: 'av-collection-table__selection-cell', locked: true },
      { id: 'name', label: t('customers.table.name'), sortable: true, locked: true },
      { id: 'category', label: t('customers.table.category') },
      { id: 'contact', label: t('customers.table.contact') },
      { id: 'phone', label: t('customers.table.phone') },
      { id: 'actions', label: '', align: 'right', locked: true }
    ],
    [t]
  );
  const visibleCustomerColumns = useMemo(
    () => resolveVisibleCollectionColumns(customerColumns, preferences.collectionColumnVisibility.customers),
    [customerColumns, preferences.collectionColumnVisibility.customers]
  );
  const selectionItems = useMemo(() => (collection ? selectCustomerSelectionItems(collection) : []), [collection]);
  const visibleIds = useMemo(() => rows.map((row) => row.id), [rows]);
  const visibleIdsKey = visibleIds.join('|');
  const queryKey = `${statusFilter}|${categoryFilter}|${sort}|${sortDirection}|${preferences.customersViewMode}`;
  const quickAddContext = createQuickAddContext({
    customers: collection?.customers ?? [],
    customerCategories: collection?.customerCategories ?? [],
    t
  });

  useEffect(() => {
    syncSelectionQuery(queryKey, visibleIds);
  }, [queryKey, syncSelectionQuery, visibleIds, visibleIdsKey]);

  async function handleDeleteSelected(customerIds: string[]): Promise<void> {
    if (customerIds.length === 0) {
      return;
    }

    setMutationError(null);

    try {
      const nextCollection = await deleteCustomers({ ids: customerIds });
      setCollection(nextCollection);
      clearSelection();
      setCustomerBulkDraft(createInitialCustomerBulkDraft());
    } catch (nextError) {
      setMutationError(nextError instanceof Error ? nextError.message : 'Customer delete failed.');
    }
  }

  useEffect(
    () =>
      subscribeToShellDeleteRequests((detail) => {
        if (detail.domain !== 'customers' || detail.ids.length === 0) {
          return;
        }

        void handleDeleteSelected(detail.ids);
      }),
    [clearSelection]
  );

  if (error) {
    return (
      <div className="av-page">
        <EmptyCollectionState title={t('customers.title')} description={error} />
      </div>
    );
  }

  if (!collection || !summary) {
    return <div className="av-page" />;
  }

  const selectedItems = selectionSession.selectedIds
    .map((selectedId) => selectionItems.find((item) => item.id === selectedId) ?? null)
    .filter((item): item is NonNullable<typeof item> => item !== null);
  const bulkCategoryOptions = selectCustomerCategoryOptions(collection, t).filter(
    (option) => option.value !== 'all' && option.value !== 'unassigned' && !option.description
  );
  const hasCustomerBulkDraftChanges = Object.values(customerBulkDraft).some((patch) => patch.mode !== 'unchanged');
  const isCustomerBulkDraftValid =
    customerBulkDraft.customerCategoryId.mode !== 'set' || customerBulkDraft.customerCategoryId.value.length > 0;
  const tableSortColumnId = customerSortToColumnId(sort);
  const tableSortState: CollectionSortState | undefined = tableSortColumnId
    ? {
        columnId: tableSortColumnId,
        direction: sortDirection
      }
    : undefined;

  function handleToggleSelection(customerId: string, event: MouseEvent<HTMLButtonElement>): void {
    event.preventDefault();
    event.stopPropagation();
    toggleSelection(customerId, visibleIds, queryKey, {
      additive: true,
      range: event.shiftKey
    });
  }

  function updateCustomerBulkDraft(patch: Partial<CustomerBulkDraft>): void {
    setCustomerBulkDraft((current) => ({
      ...current,
      ...patch
    }));
  }

  async function handleApplyBulkChanges(): Promise<void> {
    if (selectedItems.length === 0 || !hasCustomerBulkDraftChanges || !isCustomerBulkDraftValid) {
      return;
    }

    setMutationError(null);

    try {
      const nextCollection = await applyBulkCustomerPatch({
        customerIds: selectionSession.selectedIds,
        ...customerBulkDraft
      });

      setCollection(nextCollection);
      clearSelection();
      setCustomerBulkDraft(createInitialCustomerBulkDraft());
    } catch (nextError) {
      setMutationError(nextError instanceof Error ? nextError.message : 'Customer update failed.');
    }
  }

  function handleTableSortChange(nextSortState: CollectionSortState): void {
    const nextSort = customerColumnIdToSort(nextSortState.columnId);

    if (!nextSort) {
      return;
    }

    setSort(nextSort);
    setSortDirection(nextSortState.direction);
  }

  function handleSortSelectChange(nextSort: CustomersSort): void {
    setSort(nextSort);
    setSortDirection(getDefaultCustomerSortDirection(nextSort));
  }

  function handleVisibleColumnIdsChange(columnIds: string[]): void {
    void savePreferences({
      collectionColumnVisibility: {
        ...preferences.collectionColumnVisibility,
        customers: columnIds
      }
    });
  }

  function handleResetVisibleColumns(): void {
    const nextVisibility = { ...preferences.collectionColumnVisibility };
    delete nextVisibility.customers;
    void savePreferences({ collectionColumnVisibility: nextVisibility });
  }

  function renderPhoneCell(row: CustomerTableRowViewModel): JSX.Element {
    if (!row.phone) {
      return <span>{row.phoneLabel}</span>;
    }

    const phone = row.phone;
    return (
      <button className="av-inline-link av-inline-link-button" type="button" onClick={() => void openExternal(toTelUrl(phone))}>
        <Icon name="phone" />
        <span>{row.phoneLabel}</span>
      </button>
    );
  }

  function renderCustomerCell(row: CustomerTableRowViewModel, columnId: string): JSX.Element {
    const selected = selectionSession.selectedIds.includes(row.id);

    switch (columnId) {
      case 'selection':
        return (
          <td key={columnId} className="av-collection-table__cell av-collection-table__selection-cell">
            <CollectionSelectionToggle
              selected={selected}
              icon="customers"
              label={row.name}
              onClick={(event) => handleToggleSelection(row.id, event)}
            />
          </td>
        );
      case 'name':
        return (
          <td key={columnId} className="av-collection-table__cell">
            <div className="av-collection-table__identity-copy">
              <strong>
                <Link className="av-inline-link" to={`/customers/${row.id}`}>
                  <span>{row.name}</span>
                </Link>
              </strong>
              <StatusBadge label={row.statusLabel} tone={row.statusTone} />
            </div>
          </td>
        );
      case 'category':
        return (
          <td key={columnId} className="av-collection-table__cell">
            <span className="av-category-chip">
              <Icon name={row.categoryIconName} />
              <span>{row.categoryLabel}</span>
              {row.categoryArchived ? <em>{t('common.archived')}</em> : null}
            </span>
          </td>
        );
      case 'contact':
        return (
          <td key={columnId} className="av-collection-table__cell">
            {row.contactName}
          </td>
        );
      case 'phone':
        return (
          <td key={columnId} className="av-collection-table__cell">
            {renderPhoneCell(row)}
          </td>
        );
      case 'actions':
        return (
          <td key={columnId} className="av-collection-table__cell is-right">
            <OverflowMenuButton onClick={(event) => event.stopPropagation()} />
          </td>
        );
      default:
        return <td key={columnId} className="av-collection-table__cell" />;
    }
  }

  return (
    <div className="av-page">
      <SectionHeader
        title={t('customers.title')}
        subtitle={`${t('customers.summary.active', { count: summary.activeCount })} • ${t('customers.summary.archived', {
          count: summary.archivedCount
        })}`}
      />

      <InlineQuickAddPanel domain="customers" context={quickAddContext} onCreated={async () => setCollection(await listCustomers())} />

      <CollectionToolbar
        primary={
          <ToolbarGroup>
            {(['all', 'active', 'inactive', 'lead', 'archived'] as const).map((filter) => (
              <FilterChip
                key={filter}
                active={statusFilter === filter}
                label={t(`customers.filters.${filter}`)}
                onClick={() => setStatusFilter(filter)}
              />
            ))}
          </ToolbarGroup>
        }
        secondary={
          <ToolbarGroup>
            <SearchableSelectField
              clearLabel={t('common.clear')}
              emptyLabel={t('common.noResults')}
              options={selectCustomerCategoryOptions(collection, t)}
              placeholder={t('customers.filters.categoryPlaceholder')}
              searchPlaceholder={t('customers.filters.searchCategory')}
              value={categoryFilter}
              onValueChange={(value) => setCategoryFilter(value || 'all')}
            />
            <SelectField
              options={[
                { value: 'recent', label: t('customers.sort.recent') },
                { value: 'name', label: t('customers.sort.name') }
              ]}
              value={sort}
              onChange={(event) => handleSortSelectChange(event.target.value as CustomersSort)}
            />
            <CollectionViewToggle
              options={[
                { value: 'list', label: t('common.view.list'), icon: 'list' },
                { value: 'grid', label: t('common.view.grid'), icon: 'grid' }
              ]}
              value={preferences.customersViewMode}
              onChange={(value) => void setViewMode(value)}
            />
            <IconButton
              icon="settings"
              label={t('common.columns.settingsButton')}
              onClick={() => setColumnSettingsOpen(true)}
            />
          </ToolbarGroup>
        }
      />

      {mutationError ? <div className="av-inline-error">{mutationError}</div> : null}

      <SplitWorkspaceLayout
        railMode="fixed"
        main={
          rows.length === 0 ? (
            <EmptyCollectionState title={t('customers.title')} description={t('common.noResults')} />
          ) : preferences.customersViewMode === 'grid' ? (
            <CardGridSurface mode="grid">
              {rows.map((row) => {
                const selected = selectionSession.selectedIds.includes(row.id);

                return (
                  <PanelCard
                    key={row.id}
                    actions={
                      <OverflowMenuButton
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                      />
                    }
                    className={['av-collection-card', selected ? 'is-selected' : ''].filter(Boolean).join(' ')}
                  >
                    <div className="av-project-card__header">
                      <div className="av-project-card__identity">
                        <div className="av-project-card__icon-wrap">
                          <div className="av-project-card__icon">
                            <Icon name="customers" />
                          </div>
                          <CollectionSelectionToggle
                            selected={selected}
                            className="av-project-card__selection av-card-selection-toggle"
                            icon="customers"
                            variant="check"
                            label={row.name}
                            data-testid="av-card-selection-toggle"
                            onClick={(event) => handleToggleSelection(row.id, event)}
                          />
                        </div>
                        <div>
                          <h2>
                            <Link className="av-inline-link" to={`/customers/${row.id}`}>
                              <span>{row.name}</span>
                            </Link>
                          </h2>
                          <div className="av-project-card__meta">
                            <StatusBadge label={row.statusLabel} tone={row.statusTone} />
                            <span className="av-category-chip">
                              <Icon name={row.categoryIconName} />
                              <span>{row.categoryLabel}</span>
                              {row.categoryArchived ? <em>{t('common.archived')}</em> : null}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="av-project-card__footer">
                      <div className="av-summary-stack">
                        <strong>{row.contactName}</strong>
                        {row.phone ? (
                          (() => {
                            const phone = row.phone;
                            return (
                          <button
                            className="av-inline-link av-inline-link-button"
                            type="button"
                            onClick={() => void openExternal(toTelUrl(phone))}
                          >
                            <Icon name="phone" />
                            <span>{row.phoneLabel}</span>
                          </button>
                            );
                          })()
                        ) : (
                          <span>{row.phoneLabel}</span>
                        )}
                      </div>
                    </div>
                  </PanelCard>
                );
              })}
            </CardGridSurface>
          ) : (
            <DataTableSurface>
              <CollectionTable
                ariaLabel={t('customers.title')}
                columns={visibleCustomerColumns}
                sortState={tableSortState}
                onSortChange={handleTableSortChange}
              >
                {rows.map((row) => {
                  const selected = selectionSession.selectedIds.includes(row.id);

                  return (
                    <tr
                      key={row.id}
                      className={['av-collection-table__row', selected ? 'is-selected' : ''].filter(Boolean).join(' ')}
                    >
                      {visibleCustomerColumns.map((column) => renderCustomerCell(row, column.id))}
                    </tr>
                  );
                })}
              </CollectionTable>
            </DataTableSurface>
          )
        }
        rail={
          selectedItems.length > 0 ? (
            <BulkEditorRail
              title={t('customers.selection.title', { count: selectedItems.length })}
              hiddenSelectedCount={selectionSession.hiddenSelectedCount}
              items={selectedItems.map((item) => ({
                id: item.id,
                icon: 'customers',
                label: item.label,
                subtitle: item.subtitle
              }))}
              onClose={() => {
                clearSelection();
                setCustomerBulkDraft(createInitialCustomerBulkDraft());
              }}
              onRemoveItem={(itemId) => removeSelectedId(itemId, visibleIds)}
              destructiveAction={{
                label: t('shell.delete'),
                onClick: () => void handleDeleteSelected(selectionSession.selectedIds)
              }}
              primaryAction={{
                label: t('common.applyChanges'),
                disabled: !hasCustomerBulkDraftChanges || !isCustomerBulkDraftValid,
                onClick: () => void handleApplyBulkChanges()
              }}
              secondaryAction={{
                label: t('common.cancel'),
                onClick: () => {
                  clearSelection();
                  setCustomerBulkDraft(createInitialCustomerBulkDraft());
                }
              }}
            >
              <div className="av-bulk-editor-form">
                <label className="av-form-field">
                  <span>{t('customers.editor.fields.status')}</span>
                  <SelectField
                    options={[
                      { value: 'unchanged', label: t('common.leaveUnchanged') },
                      ...customerStatuses.map((status) => ({
                        value: status,
                        label: t(`enums.customerStatus.${status}`)
                      }))
                    ]}
                    value={customerBulkDraft.status.mode === 'set' ? customerBulkDraft.status.value : 'unchanged'}
                    onChange={(event) =>
                      updateCustomerBulkDraft({
                        status:
                          event.target.value === 'unchanged'
                            ? { mode: 'unchanged' }
                            : { mode: 'set', value: event.target.value as CustomerStatus }
                      })
                    }
                  />
                </label>

                <label className="av-form-field">
                  <span>{t('customers.editor.fields.customerType')}</span>
                  <SelectField
                    options={[
                      { value: 'unchanged', label: t('common.leaveUnchanged') },
                      ...customerTypes.map((type) => ({
                        value: type,
                        label: t(`enums.customerType.${type}`)
                      }))
                    ]}
                    value={customerBulkDraft.type.mode === 'set' ? customerBulkDraft.type.value : 'unchanged'}
                    onChange={(event) =>
                      updateCustomerBulkDraft({
                        type:
                          event.target.value === 'unchanged'
                            ? { mode: 'unchanged' }
                            : { mode: 'set', value: event.target.value as CustomerType }
                      })
                    }
                  />
                </label>

                <label className="av-form-field">
                  <span>{t('customers.editor.fields.customerCategory')}</span>
                  <SelectField
                    options={[
                      { value: 'unchanged', label: t('common.leaveUnchanged') },
                      { value: 'clear', label: t('common.clearValue') },
                      { value: 'set', label: t('common.setValue') }
                    ]}
                    value={customerBulkDraft.customerCategoryId.mode}
                    onChange={(event) => {
                      const mode = event.target.value as PatchMode;
                      updateCustomerBulkDraft({
                        customerCategoryId:
                          mode === 'set'
                            ? { mode: 'set', value: bulkCategoryOptions[0]?.value ?? '' }
                            : { mode }
                      });
                    }}
                  />
                  {customerBulkDraft.customerCategoryId.mode === 'set' ? (
                    <SearchableSelectField
                      clearLabel={t('common.clear')}
                      emptyLabel={t('common.noResults')}
                      options={bulkCategoryOptions}
                      placeholder={t('customers.filters.categoryPlaceholder')}
                      searchPlaceholder={t('customers.filters.searchCategory')}
                      value={customerBulkDraft.customerCategoryId.value}
                      onValueChange={(value) => updateCustomerBulkDraft({ customerCategoryId: { mode: 'set', value } })}
                    />
                  ) : null}
                </label>

                <label className="av-form-field">
                  <span>{t('customers.editor.fields.vatPayer')}</span>
                  <SelectField
                    options={[
                      { value: 'unchanged', label: t('common.leaveUnchanged') },
                      { value: 'true', label: t('common.yes') },
                      { value: 'false', label: t('common.no') }
                    ]}
                    value={customerBulkDraft.vatPayer.mode === 'set' ? String(customerBulkDraft.vatPayer.value) : 'unchanged'}
                    onChange={(event) =>
                      updateCustomerBulkDraft({
                        vatPayer:
                          event.target.value === 'unchanged'
                            ? { mode: 'unchanged' }
                            : { mode: 'set', value: event.target.value === 'true' }
                      })
                    }
                  />
                </label>
              </div>
            </BulkEditorRail>
          ) : null
        }
      />

      <CollectionColumnSettingsModal
        columns={customerColumns}
        domain="customers"
        isOpen={columnSettingsOpen}
        strings={{
          title: t('common.columns.settingsTitle'),
          description: t('common.columns.settingsDescription'),
          reset: t('common.columns.settingsReset'),
          close: t('common.close'),
          locked: t('common.columns.settingsLocked')
        }}
        visibleColumnIds={preferences.collectionColumnVisibility.customers}
        onClose={() => setColumnSettingsOpen(false)}
        onReset={handleResetVisibleColumns}
        onVisibleColumnIdsChange={handleVisibleColumnIdsChange}
      />
    </div>
  );
}
